"use client";

import { ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import clsx from "clsx";

/**
 * Generic sortable list with a drag handle, keyboard reorder (Space + ↑/↓),
 * and an `emptyState` slot for when the list is empty.
 *
 * Used by every per-type builder for choices, pairs, tokens, rubric rows,
 * and acceptable variants. Keyboard accessible by default:
 *   - Tab to a drag handle → Space to grab → ↑/↓ to move → Space to drop
 *
 * The component is intentionally controlled: callers own the items state and
 * apply the reorder on `onReorder`. This keeps the component free of any
 * business logic and lets it drop into the existing rubric editor without
 * rewriting state management.
 */
export interface SortableListProps<T> {
  items: T[];
  /** Stable key for each item — usually an id or the array index. */
  getKey: (item: T, index: number) => string;
  onReorder: (next: T[]) => void;
  /** Renders each row. Receives `{ item, index, dragHandle }` so the caller
   *  controls row markup while we own the drag handle + accessibility wiring. */
  renderRow: (args: { item: T; index: number; dragHandle: ReactNode }) => ReactNode;
  /** Empty state — rendered when `items.length === 0`. */
  emptyState?: ReactNode;
  /** Optional className applied to the outer `<ul>`. */
  className?: string;
  /** Optional className applied to each row. */
  rowClassName?: string;
  /** Optional className applied to the drag handle button. */
  dragHandleClassName?: string;
  /** Disable keyboard reordering (e.g. inside a controlled form). */
  disableKeyboard?: boolean;
}

export function SortableList<T>({
  items,
  getKey,
  onReorder,
  renderRow,
  emptyState,
  className,
  rowClassName,
  dragHandleClassName,
  disableKeyboard,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      disabled: disableKeyboard,
    }),
  );

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item, i) => getKey(item, i) === active.id);
    const newIndex = items.findIndex((item, i) => getKey(item, i) === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  }

  if (items.length === 0 && emptyState) {
    return <div className="rounded-lg border border-dashed border-[rgba(255,255,255,0.08)] p-4">{emptyState}</div>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map(getKey)}
        strategy={verticalListSortingStrategy}
      >
        <ul className={clsx("flex flex-col gap-2", className)}>
          {items.map((item, index) => (
            <SortableRow
              key={getKey(item, index)}
              id={getKey(item, index)}
              className={rowClassName}
              handleClassName={dragHandleClassName}
            >
              {renderRow({ item, index, dragHandle: <DragHandle className={dragHandleClassName} /> })}
            </SortableRow>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

/**
 * Single sortable row. Wraps the caller's markup in a `<li>` with the
 * transform styles from dnd-kit applied. The drag handle is a separate
 * element rendered via the `dragHandle` prop so the rest of the row
 * stays clickable (e.g. for a delete button).
 */
function SortableRow({
  id,
  children,
  className,
  handleClassName,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  handleClassName?: string;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  return (
    <li ref={setNodeRef} style={style} className={clsx("touch-none", className)}>
      <div
        className={clsx(
          "rounded-lg border bg-[#141414] transition-shadow",
          isDragging
            ? "border-[#FFD60A]/60 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
            : "border-[rgba(255,255,255,0.06)]",
        )}
      >
        <div className="flex items-stretch gap-2 p-2">
          {/* Drag handle — wrapped to keep the click target small and accessible. */}
          <button
            type="button"
            aria-label="Drag to reorder"
            className={clsx(
              "shrink-0 cursor-grab active:cursor-grabbing rounded-md p-1.5 text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FFD60A]",
              handleClassName,
            )}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={14} />
          </button>
          <div className="flex-1 min-w-0">{children}</div>
        </div>
      </div>
    </li>
  );
}

/**
 * Standalone drag-handle button. Use this when you want the drag affordance
 * in your own markup (the default `renderRow` already gives you one — this
 * is for advanced layouts).
 */
function DragHandle({ className }: { className?: string }) {
  return (
    <button
      type="button"
      aria-label="Drag to reorder"
      className={clsx(
        "shrink-0 cursor-grab active:cursor-grabbing rounded-md p-1.5 text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/5",
        className,
      )}
    >
      <GripVertical size={14} />
    </button>
  );
}